(function (root, factory) {
  var api = factory(root);

  root.PjDesignLabPlacement = api;

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  var CURRENT_VERSION = 1;
  var DEFAULT_MAX_PETS = 3;
  var CONFIG_ID = 'pjDesignLabPlacementConfig';
  var REQUIRED_COLORS_ID = 'pjDesignLabPlacementRequiredColors';

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function toNumber(value) {
    var number = Number(value);
    return Number.isFinite(number) ? number : NaN;
  }

  function inPercentRange(value) {
    return Number.isFinite(value) && value >= 0 && value <= 100;
  }

  function normalizeColorKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_/]+/g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function normalizeMediaId(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return '';
    var parts = raw.split('/');
    return parts[parts.length - 1] || raw;
  }

  function parseJson(raw) {
    if (raw == null || raw === '') return null;
    if (isObject(raw) || Array.isArray(raw)) return raw;
    if (typeof raw !== 'string') return null;

    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function parseSafeArea(raw) {
    if (!isObject(raw)) return null;

    var area = {
      x: toNumber(raw.x),
      y: toNumber(raw.y),
      width: toNumber(raw.width),
      height: toNumber(raw.height),
    };

    if (
      !inPercentRange(area.x) ||
      !inPercentRange(area.y) ||
      !inPercentRange(area.width) ||
      !inPercentRange(area.height)
    ) {
      return null;
    }

    return area;
  }

  function parseSlot(raw) {
    if (!isObject(raw)) return null;

    var slot = {
      x: toNumber(raw.x),
      y: toNumber(raw.y),
      width: toNumber(raw.width),
    };

    if (!inPercentRange(slot.x) || !inPercentRange(slot.y) || !inPercentRange(slot.width)) {
      return null;
    }

    if (raw.rotation != null && raw.rotation !== '') {
      var rotation = toNumber(raw.rotation);
      if (!Number.isFinite(rotation)) return null;
      slot.rotation = rotation;
    }

    if (raw.label != null && String(raw.label).trim()) {
      slot.label = String(raw.label).trim();
    }

    var safeArea = parseSafeArea(raw.safeArea);
    if (raw.safeArea != null && !safeArea) return null;
    if (safeArea) slot.safeArea = safeArea;

    return slot;
  }

  function parseSlots(rawSlots, expectedCount) {
    if (!Array.isArray(rawSlots) || rawSlots.length !== expectedCount) return null;

    var slots = [];
    for (var index = 0; index < rawSlots.length; index += 1) {
      var slot = parseSlot(rawSlots[index]);
      if (!slot) return null;
      slots.push(slot);
    }

    return slots;
  }

  function parseTemplate(raw, maxPets) {
    if (!isObject(raw)) return null;

    var color = raw.color != null ? String(raw.color).trim() : '';
    var colorKey = normalizeColorKey(raw.colorKey || color);
    if (!colorKey) return null;

    var mediaId = normalizeMediaId(raw.mediaId || raw.media_id || '');
    var slotsByCount = {};
    var sourceSlots = isObject(raw.slotsByCount) ? raw.slotsByCount : raw.slots;

    for (var count = 1; count <= maxPets; count += 1) {
      var slots = parseSlots(sourceSlots && sourceSlots[String(count)], count);
      if (slots) slotsByCount[String(count)] = slots;
    }

    return {
      color: color || colorKey,
      colorKey: colorKey,
      mediaId: mediaId,
      imageUrl: raw.imageUrl ? String(raw.imageUrl).trim() : '',
      slotsByCount: slotsByCount,
    };
  }

  function parseCatalog(raw) {
    var parsed = parseJson(raw);
    if (!isObject(parsed)) {
      return { ok: false, reason: 'missing-catalog', version: CURRENT_VERSION, maxPets: DEFAULT_MAX_PETS, templates: [] };
    }

    var version = toNumber(parsed.version);
    if (!Number.isFinite(version) || version < 1) version = CURRENT_VERSION;

    var maxPets = toNumber(parsed.maxPets);
    if (!Number.isFinite(maxPets) || maxPets < 1) maxPets = DEFAULT_MAX_PETS;
    if (maxPets > DEFAULT_MAX_PETS) maxPets = DEFAULT_MAX_PETS;

    var sourceTemplates = Array.isArray(parsed.templates) ? parsed.templates : [];
    var templates = [];
    for (var index = 0; index < sourceTemplates.length; index += 1) {
      var template = parseTemplate(sourceTemplates[index], maxPets);
      if (template) templates.push(template);
    }

    return {
      ok: true,
      version: version,
      maxPets: maxPets,
      productHandle: parsed.productHandle ? String(parsed.productHandle) : '',
      templates: templates,
    };
  }

  function findTemplate(catalog, query) {
    var colorKey = normalizeColorKey(query && query.color);
    var mediaId = normalizeMediaId(query && query.mediaId);
    var templates = catalog && Array.isArray(catalog.templates) ? catalog.templates : [];
    var colorMatches = [];

    for (var index = 0; index < templates.length; index += 1) {
      var template = templates[index];
      var colorMatch = colorKey && template.colorKey === colorKey;
      var mediaMatch = mediaId && template.mediaId && template.mediaId === mediaId;

      if (colorMatch) colorMatches.push(template);
      if (!colorKey && mediaMatch) return template;
    }

    // Products split one-per-color have no color axis to read, so a lone
    // template is unambiguous and owns the whole product.
    if (!colorKey && !mediaId && templates.length === 1) return templates[0];

    if (colorMatches.length === 1) return colorMatches[0];
    if (colorMatches.length > 1 && mediaId) {
      for (var matchIndex = 0; matchIndex < colorMatches.length; matchIndex += 1) {
        if (colorMatches[matchIndex].mediaId === mediaId) return colorMatches[matchIndex];
      }
    }

    return colorMatches[0] || null;
  }

  function getSlots(catalog, query) {
    var parsed = catalog && catalog.ok ? catalog : parseCatalog(catalog);
    if (!parsed.ok) {
      return {
        ok: false,
        reason: parsed.reason || 'missing-catalog',
        colorKey: normalizeColorKey(query && query.color),
        petCount: Number(query && query.petCount) || 0,
        slots: [],
      };
    }

    var petCount = Math.floor(toNumber(query && query.petCount));
    if (!Number.isFinite(petCount) || petCount < 1 || petCount > parsed.maxPets) {
      return {
        ok: false,
        reason: 'unsupported-pet-count',
        colorKey: normalizeColorKey(query && query.color),
        petCount: petCount,
        slots: [],
      };
    }

    var template = findTemplate(parsed, query || {});
    if (!template) {
      return {
        ok: false,
        reason: 'missing-template',
        colorKey: normalizeColorKey(query && query.color),
        petCount: petCount,
        slots: [],
      };
    }

    var slots = template.slotsByCount[String(petCount)];
    if (!slots) {
      return {
        ok: false,
        reason: 'missing-slots',
        colorKey: template.colorKey,
        mediaId: template.mediaId,
        petCount: petCount,
        slots: [],
      };
    }

    return {
      ok: true,
      reason: '',
      version: parsed.version,
      color: template.color,
      colorKey: template.colorKey,
      mediaId: template.mediaId,
      imageUrl: template.imageUrl,
      petCount: petCount,
      slots: slots,
    };
  }

  function checkCoverage(catalog, requiredColors) {
    var parsed = catalog && catalog.ok ? catalog : parseCatalog(catalog);
    var colors = Array.isArray(requiredColors) ? requiredColors : [];
    var missing = [];
    var covered = [];

    if (!parsed.ok) {
      return { complete: colors.length === 0, missing: colors.map(function (color) {
        return { color: color, reason: 'missing-catalog' };
      }), covered: [] };
    }

    for (var index = 0; index < colors.length; index += 1) {
      var color = colors[index];
      var colorMissing = false;
      var reason = '';

      for (var petCount = 1; petCount <= parsed.maxPets; petCount += 1) {
        var result = getSlots(parsed, { color: color, petCount: petCount });
        if (!result.ok) {
          colorMissing = true;
          reason = result.reason;
          break;
        }
      }

      if (colorMissing) missing.push({ color: color, reason: reason });
      else covered.push(color);
    }

    return {
      complete: missing.length === 0,
      missing: missing,
      covered: covered,
    };
  }

  function readScriptJson(doc, id) {
    if (!doc || typeof doc.getElementById !== 'function') return null;
    var node = doc.getElementById(id);
    if (!node) return null;
    return parseJson(node.textContent);
  }

  function loadFromDocument(doc) {
    var catalog = parseCatalog(readScriptJson(doc, CONFIG_ID));
    var requiredColors = readScriptJson(doc, REQUIRED_COLORS_ID);
    var coverage = checkCoverage(catalog, Array.isArray(requiredColors) ? requiredColors : []);

    return {
      catalog: catalog,
      coverage: coverage,
    };
  }

  // QA/support entry point: explains why a color resolves or does not, without
  // needing a debugger. Call PjDesignLabPlacement.debug() in the console.
  function diagnose(doc) {
    var target = doc || root.document;
    var node =
      target && typeof target.getElementById === 'function'
        ? target.getElementById(CONFIG_ID)
        : null;
    var raw = node ? String(node.textContent || '').trim() : '';
    var parsedRaw = parseJson(raw);
    var catalog = parseCatalog(parsedRaw);
    var requiredColors = readScriptJson(target, REQUIRED_COLORS_ID);
    var colors = Array.isArray(requiredColors) ? requiredColors : [];

    var rawType = 'empty-or-invalid-json';
    if (typeof parsedRaw === 'string') {
      rawType =
        'string — double-encoded, so the metafield is probably Single line text rather than JSON; tolerated but worth fixing';
    } else if (Array.isArray(parsedRaw)) {
      rawType = 'array — expected an object with a templates array';
    } else if (isObject(parsedRaw)) {
      rawType = 'object';
    }

    return {
      configScriptFound: Boolean(node),
      metafieldPresent: raw !== '' && raw !== 'null',
      metafieldValueType: rawType,
      rawLength: raw.length,
      rawPreview: raw.slice(0, 300),
      parsedOk: catalog.ok,
      parseReason: catalog.reason || '',
      version: catalog.version,
      maxPets: catalog.maxPets,
      templates: (catalog.templates || []).map(function (template) {
        return {
          color: template.color,
          colorKey: template.colorKey,
          mediaId: template.mediaId,
          petCountsConfigured: Object.keys(template.slotsByCount),
        };
      }),
      productColorOptions: colors,
      coverage: checkCoverage(catalog, colors),
    };
  }

  function debug(doc) {
    var report = diagnose(doc);
    if (root.console && typeof root.console.log === 'function') {
      root.console.log('[PJ Design Lab] placement diagnostics', report);
    }
    return report;
  }

  var loaded = null;

  function load() {
    loaded = loadFromDocument(root.document);
    return loaded;
  }

  function current() {
    return loaded || load();
  }

  if (root.document) load();

  return {
    CURRENT_VERSION: CURRENT_VERSION,
    DEFAULT_MAX_PETS: DEFAULT_MAX_PETS,
    CONFIG_ID: CONFIG_ID,
    REQUIRED_COLORS_ID: REQUIRED_COLORS_ID,
    normalizeColorKey: normalizeColorKey,
    normalizeMediaId: normalizeMediaId,
    parseCatalog: parseCatalog,
    getSlots: getSlots,
    checkCoverage: checkCoverage,
    loadFromDocument: loadFromDocument,
    diagnose: diagnose,
    debug: debug,
    load: load,
    current: current,
  };
});
